import { getFresh } from './cep';

export interface SourceLocation {
  source: string;
  line: number;
}

interface LineMapArtifact {
  sources: string[];
  lines: (readonly [number, number] | null)[];
}

function isLineMapArtifact(value: unknown): value is LineMapArtifact {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const candidate = value as Record<string, unknown>;
  return Array.isArray(candidate.sources) && Array.isArray(candidate.lines);
}

export type SourceLocationResolver = (bundleLine: number) => Promise<SourceLocation | undefined>;

/*
  The line map is a build artifact written next to the bundle by
  scripts/lineMap.js: it maps a physical line of the loaded .jsx bundle to the
  exact original .ts file and line it came from. It is fetched lazily, since
  most calls never fail and paying for the request up front would be wasted
  work on the hot path.
*/
export function createSourceLocationResolver(bundlePath: string): SourceLocationResolver {
  let artifactPromise: Promise<LineMapArtifact | undefined> | undefined;

  function loadArtifact(): Promise<LineMapArtifact | undefined> {
    if (!artifactPromise) {
      artifactPromise = getFresh(`${bundlePath}.linemap.json`)
        .then((raw: unknown): LineMapArtifact | undefined => {
          const parsed: unknown = typeof raw === 'string' ? JSON.parse(raw) : raw;
          return isLineMapArtifact(parsed) ? parsed : undefined;
        })
        .catch(() => {
          artifactPromise = undefined;
          return undefined;
        });
    }
    return artifactPromise;
  }

  return async (bundleLine: number) => {
    const artifact = await loadArtifact();
    if (!artifact) {
      return undefined;
    }
    const entry = artifact.lines[bundleLine - 1];
    if (!entry) {
      return undefined;
    }
    const [sourceIndex, line] = entry;
    const source = artifact.sources[sourceIndex];
    return source === undefined ? undefined : { source, line };
  };
}
